import React, { useState, useEffect, useRef } from 'react';
import { Container, Form, Button, Alert, Card, Row, Col, Badge } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../context/PageTitleContext';
import { useNavigate } from 'react-router-dom';

function AddRestaurantForm() {
  const { setPageTitle } = usePageTitle();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website_url: '',
    flickr_album_url: '',
    address: '',
    unit: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'United States',
    phone: '',
    location_name: '',   // #159: optional label for the location, e.g. "Downtown"
    // #140: coordinates from the Google Places picker, sent with the location so
    // the restaurant is geocoded at creation (no dependency on admin moderation).
    latitude: null,
    longitude: null,
    // #166: the picked place's id + its opening hours, same principle as the
    // coords above — take what Google already gave us instead of depending on
    // admin curation that private restaurants never receive.
    google_place_id: null,
    monday_hours: '',
    tuesday_hours: '',
    wednesday_hours: '',
    thursday_hours: '',
    friday_hours: '',
    saturday_hours: '',
    sunday_hours: '',
    category_ids: [],
    cuisine_ids: [],
  });

  // #137: submit to the public/curated directory (admin-reviewed) vs keep the
  // restaurant private ("just for me" — e.g. a chain you track privately).
  const [submitForPublic, setSubmitForPublic] = useState(true);
  const [categories, setCategories] = useState([]);
  const [cuisines, setCuisines] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // #77 — add-from-Google place picker. Two explicit paths: "Find near me"
  // uses location to *select* results; "Search by name" uses it to *rank* them
  // (#246).
  //
  // This once read "the app never infers the user's location", which is the most
  // likely reason search-by-name shipped with no coordinates. It doesn't hold up:
  // we proxy the call to Google, and Google biases an unbiased request by the
  // caller's IP — so withholding the user's location didn't mean "no location",
  // it meant "our server's location", which is in New Jersey.
  const [placeMode, setPlaceMode] = useState(null); // null | 'search' | 'nearby'
  const [placeQuery, setPlaceQuery] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [nearbyPlaces, setNearbyPlaces] = useState([]);
  const [placeLoading, setPlaceLoading] = useState(false);
  const [placeError, setPlaceError] = useState('');
  const [prefilledFrom, setPrefilledFrom] = useState('');
  // #246 — true when the last by-name search went out with no coordinates, so it
  // was ranked around our server. Biased and unbiased results are otherwise
  // indistinguishable (real places, plausible names, wrong region), which is why
  // this went unreported for so long — hence the hint rather than silence.
  const [searchWasUnbiased, setSearchWasUnbiased] = useState(false);
  // Refs, not state: the geolocation callback fires outside React's render cycle
  // and must see the current values, not the ones closed over when the search
  // that triggered it was issued.
  const searchCoordsRef = useRef(null);
  const locationAskedRef = useRef(false);
  const placeQueryRef = useRef('');
  const [sessionToken] = useState(() =>
    (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : String(Math.random())
  );

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  useEffect(() => {
    // Fetch categories and cuisines
    fetchCategories();
    fetchCuisines();
  }, []);

  // Set page title for Add Restaurant page
  useEffect(() => {
    setPageTitle('Add Restaurant');
  }, [setPageTitle]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/restaurants/categories/`);
      if (response.ok) {
        const data = await response.json();
        // Sort alphabetically by name for consistent display
        const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));
        setCategories(sortedData);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchCuisines = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/cuisines/`);
      if (response.ok) {
        const data = await response.json();
        // Sort alphabetically by name for consistent display (redundant but defensive)
        const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));
        setCuisines(sortedData);
      }
    } catch (err) {
      console.error('Error fetching cuisines:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    // #140: if the user hand-edits an address field, drop any coords captured from
    // the Places picker so they can't go stale — the backend will re-geocode from
    // the corrected address on submit.
    const clearsCoords = ['address', 'city', 'state', 'zip_code', 'country'].includes(name);
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(clearsCoords ? { latitude: null, longitude: null } : {}),
    }));
  };

  const handleMultiSelect = (e, fieldName) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => parseInt(option.value));
    setFormData(prev => ({
      ...prev,
      [fieldName]: selectedOptions
    }));
  };

  // --- #77 place-picker helpers ---
  const authPost = async (endpoint, body) => {
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.detail || 'Request failed');
    }
    return res.json();
  };

  // Ask for location on the first real by-name search, at most once per form.
  // Fire-and-forget on purpose: the search must never wait on geolocation, which
  // can take seconds, or hang until the user answers a prompt, or never resolve.
  // If a fix does arrive, it re-runs the search so the list on screen is
  // corrected — otherwise the user grants permission and then keeps staring at
  // the same server-ranked results until they happen to type another character.
  const requestSearchLocation = () => {
    if (locationAskedRef.current || searchCoordsRef.current) return;
    if (!navigator.geolocation) return;
    locationAskedRef.current = true;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        searchCoordsRef.current = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        if (placeQueryRef.current.length >= 3) runPlaceSearch(placeQueryRef.current);
      },
      // Declined or unavailable: no retry, no nag. Results stay unbiased and the
      // hint under the list says so.
      () => {},
      { timeout: 8000, maximumAge: 300000 },
    );
  };

  // Search-by-name path (backlog adds): Google Places autocomplete for businesses,
  // ranked around the user when we know where they are (#246).
  const runPlaceSearch = async (text) => {
    setPlaceQuery(text);
    placeQueryRef.current = text;
    setPlaceError('');
    if (!text || text.length < 3) { setPredictions([]); return; }
    requestSearchLocation();
    const coords = searchCoordsRef.current;
    try {
      setPlaceLoading(true);
      const data = await authPost('/api/google-maps/autocomplete', {
        input: text, types: 'establishment', session_token: sessionToken,
        ...(coords ? { latitude: coords.latitude, longitude: coords.longitude } : {}),
      });
      setPredictions(data.predictions || []);
      setSearchWasUnbiased(!coords);
    } catch (err) {
      setPlaceError(err.message);
    } finally {
      setPlaceLoading(false);
    }
  };

  // Find-near-me path (discovery): opt-in, requires the user to share location.
  const findNearby = () => {
    setPlaceError('');
    if (!navigator.geolocation) {
      setPlaceError('Location is not available in this browser — try Search by name instead.');
      return;
    }
    setPlaceLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        // #246 — keep the fix for the by-name path too. Someone who tried
        // "Find near me" first has already granted access and already waited
        // for a fix; making them do both again after switching tabs would be
        // gratuitous.
        searchCoordsRef.current = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        locationAskedRef.current = true;
        try {
          const data = await authPost('/api/google-maps/nearby', {
            latitude: pos.coords.latitude, longitude: pos.coords.longitude,
          });
          setNearbyPlaces(data.places || []);
          if ((data.places || []).length === 0) setPlaceError('No nearby restaurants found.');
        } catch (err) {
          setPlaceError(err.message);
        } finally {
          setPlaceLoading(false);
        }
      },
      () => {
        setPlaceError('Could not get your location. You can search by name instead.');
        setPlaceLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Both paths converge here: pull structured address from the chosen place and
  // prefill the form for the user to review before submitting.
  const selectPlace = async (placeId, displayName) => {
    setPlaceError('');
    try {
      setPlaceLoading(true);
      // #166: ask for hours too. They used to be declined here, which is why every
      // user-added restaurant arrived with none — and since hours otherwise only
      // come from admin curation, private restaurants (which never enter
      // moderation, #137) never got any and could never match "Open now".
      const details = await authPost('/api/google-maps/place-details', {
        place_id: placeId, session_token: sessionToken, include_hours: true,
      });
      // The backend runs parse_opening_hours internally, so opening_hours already
      // arrives keyed as { monday_hours, ... } — the location payload's own field
      // names. No mapping needed here.
      const hours = details.opening_hours || {};
      setFormData(prev => ({
        ...prev,
        name: displayName || prev.name,
        address: details.address || '',
        city: details.city || '',
        state: details.state || '',
        zip_code: details.zip_code || '',
        country: details.country || prev.country || 'United States',
        // #140: Google already resolved exact coords for the picked place — keep
        // them so the restaurant is geocoded at the source (address is validated too).
        latitude: details.latitude ?? null,
        longitude: details.longitude ?? null,
        // #166: keep the place id so a later hours/coords refresh is an exact
        // lookup rather than a fuzzy name+address search.
        google_place_id: placeId,
        monday_hours: hours.monday_hours || '',
        tuesday_hours: hours.tuesday_hours || '',
        wednesday_hours: hours.wednesday_hours || '',
        thursday_hours: hours.thursday_hours || '',
        friday_hours: hours.friday_hours || '',
        saturday_hours: hours.saturday_hours || '',
        sunday_hours: hours.sunday_hours || '',
      }));
      setPrefilledFrom(displayName || 'Google');
      setPredictions([]);
      setNearbyPlaces([]);
      setPlaceQuery('');
      setPlaceMode(null);
    } catch (err) {
      setPlaceError(err.message);
    } finally {
      setPlaceLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!isAuthenticated) {
      setError('Please login to add a restaurant');
      return;
    }

    if (!formData.name || !formData.city) {
      setError('Restaurant name and city are required');
      return;
    }

    setLoading(true);

    try {
      // Prepare the data for submission
      const restaurantData = {
        name: formData.name,
        description: formData.description || null,
        website_url: formData.website_url || null,
        flickr_album_url: formData.flickr_album_url || null,
        submit_for_public: submitForPublic,  // #137
        category_ids: formData.category_ids,
        cuisine_ids: formData.cuisine_ids,
        locations: [
          {
            name: formData.location_name?.trim() ? formData.location_name.trim() : null, // #159
            address: formData.address || '',
            unit: formData.unit || '',
            city: formData.city,
            state: formData.state || '',
            zip_code: formData.zip_code || '',
            country: formData.country || 'United States',
            phone: formData.phone || '',
            // #140: coords from the Places picker (null when the user typed the
            // address manually — the backend then geocodes it best-effort).
            latitude: formData.latitude ?? null,
            longitude: formData.longitude ?? null,
            // #166: place id + hours from the picker. All null/'' when the address
            // was typed by hand; the owner can fill them later from the Edit form.
            google_place_id: formData.google_place_id ?? null,
            monday_hours: formData.monday_hours || null,
            tuesday_hours: formData.tuesday_hours || null,
            wednesday_hours: formData.wednesday_hours || null,
            thursday_hours: formData.thursday_hours || null,
            friday_hours: formData.friday_hours || null,
            saturday_hours: formData.saturday_hours || null,
            sunday_hours: formData.sunday_hours || null,
            is_main_location: true
          }
        ]
      };

      const response = await fetch(`${API_BASE_URL}/restaurants/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(restaurantData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add restaurant');
      }

      const data = await response.json();
      setSuccess(
        submitForPublic
          ? `Restaurant "${formData.name}" submitted successfully! Your submission is pending admin approval and will appear on the site once reviewed. Redirecting to home page...`
          : `Private restaurant "${formData.name}" saved to your list. Only you can see it. Redirecting to home page...`
      );

      // Reset form
      setFormData({
        name: '',
        description: '',
        website_url: '',
        flickr_album_url: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        country: 'United States',
        phone: '',
        location_name: '',
        latitude: null,
        longitude: null,
        // #166: clear the picked place too, or a second add would inherit the
        // previous restaurant's id and hours.
        google_place_id: null,
        monday_hours: '',
        tuesday_hours: '',
        wednesday_hours: '',
        thursday_hours: '',
        friday_hours: '',
        saturday_hours: '',
        sunday_hours: '',
        category_ids: [],
        cuisine_ids: [],
      });

      // Redirect to home page after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <Container className="my-5">
        <Alert variant="info">
          Please <a href="/login">login</a> or <a href="/register">register</a> to add a restaurant.
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="my-5">
      <Card>
        <Card.Body>
          <h2 className="mb-4">Add a New Restaurant</h2>
          <p className="text-muted mb-4">
            Share your favorite restaurant with the community! All submissions require admin approval before appearing on the site.
          </p>

          {/* #77 — Find the restaurant (two explicit paths; the app never guesses your location) */}
          <Card bg="light" className="mb-4">
            <Card.Body>
              <h5 className="mb-1">Find it faster <span className="text-muted fw-normal">(optional)</span></h5>
              <p className="text-muted small mb-3">
                Adding one from your list? <strong>Search by name</strong>. Standing near a place?{' '}
                <strong>Find nearby</strong> ones not yet in the app. Either way we’ll prefill the details for you to review.
              </p>
              <div className="d-flex gap-2 mb-3">
                <Button
                  size="sm"
                  variant={placeMode === 'search' ? 'primary' : 'outline-primary'}
                  onClick={() => { setPlaceMode('search'); setNearbyPlaces([]); setPlaceError(''); }}
                >
                  🔎 Search by name
                </Button>
                <Button
                  size="sm"
                  variant={placeMode === 'nearby' ? 'primary' : 'outline-primary'}
                  onClick={() => { setPlaceMode('nearby'); setPredictions([]); setPlaceError(''); findNearby(); }}
                >
                  📍 Find near me
                </Button>
              </div>

              {placeError && (
                <Alert variant="warning" className="py-2 small" dismissible onClose={() => setPlaceError('')}>
                  {placeError}
                </Alert>
              )}

              {placeMode === 'search' && (
                <>
                  <Form.Control
                    type="text"
                    value={placeQuery}
                    onChange={(e) => runPlaceSearch(e.target.value)}
                    placeholder="Start typing a restaurant name…"
                    autoFocus
                  />
                  {placeLoading && <div className="small text-muted mt-1">Searching…</div>}
                  {predictions.length > 0 && (
                    <div className="list-group mt-2">
                      {predictions.map((p) => (
                        <button
                          type="button"
                          key={p.place_id}
                          className="list-group-item list-group-item-action"
                          onClick={() => selectPlace(p.place_id, p.structured_formatting?.main_text || p.description)}
                        >
                          <div className="fw-medium">{p.structured_formatting?.main_text || p.description}</div>
                          {p.structured_formatting?.secondary_text && (
                            <small className="text-muted">{p.structured_formatting.secondary_text}</small>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  {/* #246 — say when the list isn't ranked around the user. Without
                      this, server-ranked and user-ranked results look identical. */}
                  {searchWasUnbiased && predictions.length > 0 && (
                    <div className="small text-muted mt-2">
                      Ranked without your location — allow location access for closer matches.
                    </div>
                  )}
                </>
              )}

              {placeMode === 'nearby' && (
                <>
                  {placeLoading && <div className="small text-muted">Finding places near you…</div>}
                  {nearbyPlaces.length > 0 && (
                    <div className="list-group mt-1">
                      {nearbyPlaces.map((p) => (
                        <button
                          type="button"
                          key={p.place_id}
                          className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                          disabled={p.already_in_db}
                          onClick={() => { if (!p.already_in_db) selectPlace(p.place_id, p.name); }}
                        >
                          <span>
                            <span className="fw-medium">{p.name}</span>
                            {p.address && <small className="text-muted d-block">{p.address}</small>}
                          </span>
                          {p.already_in_db && <Badge bg="secondary">Already in app</Badge>}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}

              {prefilledFrom && (
                <Alert variant="success" className="py-2 small mt-2 mb-0">
                  Prefilled from <strong>{prefilledFrom}</strong> — review the details below and submit.
                  {/* #166: hours are captured silently (there are no hours fields on
                      this form), so say so — otherwise the only signal that Google
                      matched the wrong place would be a wrong "Open now" later. */}
                  {formData.monday_hours && (
                    <> Opening hours were included; you can edit them later from the
                    restaurant's Edit form.</>
                  )}
                </Alert>
              )}
            </Card.Body>
          </Card>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

          <Form onSubmit={handleSubmit}>
            {/* Basic Information */}
            <h5 className="mb-3">Basic Information</h5>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Restaurant Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g., Joe's Pizza"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Tell us about this restaurant..."
              />
              <Form.Text className="text-muted">
                You can add links and <strong>bold</strong> text — paste a URL, or use{' '}
                <code>[label](https://…)</code> and <code>**bold**</code>.
              </Form.Text>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Website URL</Form.Label>
                  <Form.Control
                    type="url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    placeholder="https://example.com"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Flickr Album URL</Form.Label>
                  <Form.Control
                    type="url"
                    name="flickr_album_url"
                    value={formData.flickr_album_url}
                    onChange={handleChange}
                    placeholder="https://flickr.com/photos/user/albums/..."
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Location */}
            <h5 className="mb-3 mt-4">Location</h5>
            <Form.Group className="mb-3">
              <Form.Label>Location name (optional)</Form.Label>
              <Form.Control
                type="text"
                name="location_name"
                value={formData.location_name}
                onChange={handleChange}
                placeholder='e.g. "Downtown"'
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Street Address</Form.Label>
              <Form.Control
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="123 Main Street"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Unit/Suite/Apt</Form.Label>
              <Form.Control
                type="text"
                name="unit"
                value={formData.unit}
                onChange={handleChange}
                placeholder="Suite 100"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>City <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="New York"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>State/Province/Region</Form.Label>
                  <Form.Control
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="NY"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Postal/ZIP Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="zip_code"
                    value={formData.zip_code}
                    onChange={handleChange}
                    placeholder="10001"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Country <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                  >
                    <option value="United States">United States</option>
                    <option value="Canada">Canada</option>
                    <option value="Bangladesh">Bangladesh</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="Australia">Australia</option>
                    <option value="India">India</option>
                    <option value="Mexico">Mexico</option>
                    <option value="France">France</option>
                    <option value="Germany">Germany</option>
                    <option value="Italy">Italy</option>
                    <option value="Spain">Spain</option>
                    <option value="Japan">Japan</option>
                    <option value="China">China</option>
                    <option value="South Korea">South Korea</option>
                    <option value="Thailand">Thailand</option>
                    <option value="Brazil">Brazil</option>
                    <option value="Other">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Categories and Cuisines */}
            <h5 className="mb-3 mt-4">Categories & Cuisine</h5>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Categories (hold Ctrl/Cmd to select multiple)</Form.Label>
                  <Form.Select
                    multiple
                    name="category_ids"
                    value={formData.category_ids}
                    onChange={(e) => handleMultiSelect(e, 'category_ids')}
                    size={5}
                  >
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cuisines (hold Ctrl/Cmd to select multiple)</Form.Label>
                  <Form.Select
                    multiple
                    name="cuisine_ids"
                    value={formData.cuisine_ids}
                    onChange={(e) => handleMultiSelect(e, 'cuisine_ids')}
                    size={5}
                  >
                    {cuisines.map(cuisine => (
                      <option key={cuisine.id} value={cuisine.id}>
                        {cuisine.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* #137: visibility choice — public (curated, moderated) vs private. */}
            <h5 className="mb-3 mt-4">Visibility</h5>
            <Form.Group className="mb-3">
              <Form.Check
                type="radio"
                id="visibility-public"
                name="submitForPublic"
                label="Submit to the public directory"
                checked={submitForPublic}
                onChange={() => setSubmitForPublic(true)}
              />
              <div className="text-muted small ms-4 mb-2">
                Shared with everyone after an admin reviews it. Best for real, non-chain spots others should discover.
              </div>
              <Form.Check
                type="radio"
                id="visibility-private"
                name="submitForPublic"
                label="Keep private — just for me"
                checked={!submitForPublic}
                onChange={() => setSubmitForPublic(false)}
              />
              <div className="text-muted small ms-4">
                Only you can see it — it won't be reviewed or shown publicly. Good for a chain (e.g. a location you visit) you just want to track.
              </div>
            </Form.Group>

            <div className="d-flex gap-2 mt-4">
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? 'Adding Restaurant...' : (submitForPublic ? 'Submit Restaurant' : 'Save Private Restaurant')}
              </Button>
              <Button variant="secondary" onClick={() => navigate('/')}>
                Cancel
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default AddRestaurantForm;
